import * as React from 'react'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import * as Sentry from '@sentry/browser'
import { Button, Divider, Drawer, Intent } from '@blueprintjs/core'
import { AxiosPromise } from 'axios'
import { IconNames } from '@blueprintjs/icons'
import Table from './Table'
import { AppState } from '../../../store'
import Widget from '../../../components/Widget'
import { cleanParams, readQueryParams, replaceQueryParams } from '../../../utils/pagination'
import * as Meta from './Meta'
import Poller from './Poller'
import { PaginableMeta } from '../../../types/api/paginable'
import { ListingManagerListingTypes } from '../../../types/api/listings'
import { ColumnParams } from './types'
import StateSwitcher from './StateSwitcher'
import TabStates from '../shopify/constants/TabStates'
import { DEFAULT_PER_PAGE } from './Constants'
import Client from '../../../client'
import { newConnection, updateFromProduct } from './StatusUpdate'
import PopIn from '../../../components/PopIn'
import CHANNELS from '../../../constants/Channels'
import { BulkActionBar } from './BulkActionBar'
import { S } from '../../../utils'
import SelectionManager from './SelectionManager'
import { ConnectedMarketAccount } from '../../../current/types'

export interface DialogState {
  listDialogOpen: boolean
  delistDialogOpen: boolean
  relistDialogOpen: boolean
}

export interface BulkListingDialogsProps {
  selectionManager: SelectionManager
  dialogState: DialogState
  onClose: (stateKey: keyof DialogState) => void
  marketAccountId: number
  onSubmitted: Function
  channelId?: number
}

export interface ShowListingProps {
  listingId: number | string
  onChange: any
  refreshData: any
  onClose: () => void
  marketAccount: ConnectedMarketAccount
}

interface Props {
  marketAccountId: number
  listingIndexFetchMethod: (
    marketAccountId: number,
    meta: Partial<PaginableMeta>
  ) => AxiosPromise<{
    listings: ListingManagerListingTypes[]
    meta: PaginableMeta
  }>
  tableColumns: (
    meta: PaginableMeta,
    onMetaChange: Function,
    isActive: boolean,
    openDrawer: Function,
    marketAccountId: number
  ) => ColumnParams[]
  ShowListingComponent: FC<ShowListingProps>
  BulkListingDialogs?: FC<BulkListingDialogsProps>
  channelId?: number
}

type SelectionState = {
  selection: Set<any>
  selectAll: boolean
}

export const ListingManager: FC<Props> = ({
  marketAccountId,
  listingIndexFetchMethod,
  tableColumns,
  ShowListingComponent,
  BulkListingDialogs,
  channelId
}) => {
  const connectedMarketAccounts = useSelector((state: AppState) => state.current.organization.connectedMarketAccounts)
  const currentUser = useSelector((state: AppState) => state.current.user)
  const marketAccount = useMemo(
    () => connectedMarketAccounts.find(orgMarketAccount => orgMarketAccount.id === marketAccountId),
    [marketAccountId, connectedMarketAccounts]
  )

  const initialMeta = (): PaginableMeta => {
    const queryParams = readQueryParams()
    return {
      ...Meta.InitialState(),
      viewListingId: queryParams.viewListingId
    }
  }

  const [meta, setMeta] = useState<PaginableMeta>(initialMeta())
  const [loading, setLoading] = useState(false)
  const [selectionState, setSelectionState] = useState<SelectionState>({ selection: new Set(), selectAll: false })
  const [dialogState, setDialogState] = useState<DialogState>({
    listDialogOpen: false,
    delistDialogOpen: false,
    relistDialogOpen: false
  })
  const [data, setData] = useState([])
  const [requiresAttention, setRequiresAttention] = useState(false)

  const attentionPollerRef = useRef(null)
  useEffect(() => {
    const attentionPoller = new Poller(
      () => {
        const validChannels = [CHANNELS.GOODWILLFINDS.id, CHANNELS.ETSY.id, CHANNELS.AMAZON.id]
        if (validChannels.includes(marketAccount.marketId)) {
          return Client.marketRequiresAttention({ marketAccountId })
        }

        throw Error(`Unsupported market: ${marketAccount.marketId}`)
      },
      () => ({}),
      (dataAttnReq, callback) => {
        setRequiresAttention(dataAttnReq.requiresAttention)
        callback()
      }
    )

    attentionPollerRef.current = attentionPoller

    attentionPoller.startImmediately()

    // cancel poller when component unmounts
    return () => {
      attentionPoller.cancel()
    }
  }, [marketAccountId, marketAccount.marketId])

  useEffect(() => {
    // websocket connection for listing state updates
    const subscription = newConnection(marketAccount.marketId, d => {
      setData(dataOld => {
        return updateFromProduct({ data: dataOld }, d).data
      })
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [marketAccount.marketId])

  const nextAllowedPageAfter = (page: number): number => {
    const { state, tab } = meta
    let allowedStates = state && state.split(',')
    if (!allowedStates) {
      allowedStates = tab === 'inactive-all' ? Meta.InactiveStates.split(',') : Meta.ActiveStates.split(',')
    }

    const isOutOfSync = data.reduce((acc, curr) => acc || !allowedStates.includes(curr.state), false)
    return isOutOfSync ? page : page + 1
  }

  const fetchData = useCallback(
    async metaParam => {
      const buildRequestParameters = () => {
        const state = metaParam.state || (metaParam.tab === 'inactive-all' ? Meta.InactiveStates : Meta.ActiveStates)

        return {
          ...metaParam,
          ...Meta.clearUnusedDates(metaParam),
          state
        }
      }

      setLoading(true)
      try {
        const params = cleanParams(buildRequestParameters())
        const res = await listingIndexFetchMethod(marketAccountId, params)
        attentionPollerRef?.current?.resetJustTimer()
        setMeta(oldMeta => {
          const { tab, state } = oldMeta
          const newMeta = {
            ...oldMeta,
            ...res.data.meta
          }
          newMeta.state = state
          newMeta.tab = tab
          newMeta.userId = res.data.meta.userId ? res.data.meta.userId.join(',') : null

          return newMeta
        })

        setSelectionState({ selection: new Set(), selectAll: false })
        setData(res.data.listings.map(s => ({ ...s, _id: s.id })))
      } catch (e) {
        Sentry.captureException(e)
      } finally {
        setLoading(false)
      }
    },
    [listingIndexFetchMethod, marketAccountId]
  )

  useEffect(() => {
    fetchData(meta)
    // TODO(goodwillfinds): make sure this is safe to do https://app.asana.com/0/1204859485810598/1206731189234157/f
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onMetaChange = (k, v, refetch = true) => {
    const newMeta = {
      ...meta,
      page: 1, // reset page unless overriden by [k]
      cursor: null, // reset cursor unless overridden by k
      [k]: v
    }
    setMeta(newMeta)

    if (refetch) fetchData(newMeta)
  }

  const openDrawer = listingId => {
    setMeta({ ...meta, viewListingId: listingId })
    replaceQueryParams({ viewListingId: listingId })
  }

  const closeDrawer = () => {
    openDrawer(null)
  }

  const isActive = !meta.tab || meta.tab === 'active-all'
  const columns = tableColumns(
    meta,
    onMetaChange,
    // true if active, false if inactive, null otherwise
    ['active-all', 'inactive-all', null].includes(meta.tab) ? isActive : null,
    openDrawer,
    marketAccountId
  )

  const isSelected = key => selectionState.selection.has(key)

  const onToggleAll = () => {
    setSelectionState({
      selection: selectionState.selectAll ? new Set() : new Set(data.map(d => d._id)),
      selectAll: !selectionState.selectAll
    })
  }

  const onToggleSelection = key => {
    const { selection } = selectionState

    if (selection.has(key)) {
      selection.delete(key)
    } else {
      selection.add(key)
    }

    setSelectionState({ ...selectionState, selection })
  }

  const onStateSwitcherChange = (tabId, states) => {
    setMeta(oldMeta => {
      const newMeta = {
        ...oldMeta,
        tab: tabId || 'active-all',
        state: states || null,
        page: 1,
        perPage: oldMeta.perPage || DEFAULT_PER_PAGE,
        sort: 'created_at.desc'
      }

      fetchData(newMeta)

      return newMeta
    })
  }

  // TODO: memoize
  const getAvailableActions = selectionManager => {
    const { tab } = meta
    const disabledMarkets = [CHANNELS.GOODWILLFINDS.id, CHANNELS.ETSY.id]

    if (tab === 'inactive-all') return []

    const actions = []
    if (!currentUser.permissions.includes('listings.manage')) {
      return actions
    }

    const delistableCount = selectionManager.getDelistableCount()
    const listableCount = selectionManager.getListableCount()
    const relistableCount = selectionManager.getRelistableCount()

    if (delistableCount) {
      actions.push({
        humanReadableName: `Delist (${delistableCount})`,
        onClick: () => setDialogState({ ...dialogState, delistDialogOpen: true }),
        intent: Intent.DANGER,
        icon: IconNames.EXCLUDE_ROW
      })
    }

    // GoodwillFinds & Etsy no longer support listing/relisting
    if (disabledMarkets.includes(marketAccount.marketId)) {
      return actions
    }

    // TODO(ian): make the market exclusion logic more generic, handle it higher up
    if (listableCount) {
      actions.push({
        humanReadableName: `List (${listableCount})`,
        onClick: () => setDialogState({ ...dialogState, listDialogOpen: true }),
        intent: Intent.SUCCESS,
        icon: IconNames.TICK
      })
    }

    if (relistableCount) {
      actions.push({
        humanReadableName: `Relist (${relistableCount})`,
        onClick: () => setDialogState({ ...dialogState, relistDialogOpen: true }),
        intent: Intent.PRIMARY,
        icon: IconNames.SOCIAL_MEDIA
      })
    }

    return actions
  }

  const onDialogClose = (dialogKey: keyof DialogState) => {
    setDialogState({ ...dialogState, [dialogKey]: false })
    setSelectionState({ ...selectionState, selection: new Set(), selectAll: false })
  }

  const onBulkActionDialogSubmit = async () => {
    const selectedListings = Array.from(selectionState.selection)
    await fetchData(meta)
    setData(prevData => prevData.filter(listing => !selectedListings.includes(listing.id)))
  }

  // TODO: memoize https://app.asana.com/0/1206184798330168/1206962634973833
  const selectionManager = new SelectionManager()
  selectionManager.setSelectionData(Array.from(selectionState.selection), data)

  return (
    <div className="container" style={{ maxWidth: '100%' }}>
      {BulkListingDialogs && (
        <BulkListingDialogs
          dialogState={dialogState}
          selectionManager={selectionManager}
          onClose={onDialogClose}
          marketAccountId={marketAccount.id}
          onSubmitted={onBulkActionDialogSubmit}
          channelId={channelId}
        />
      )}

      <Widget title={`Manage ${marketAccount.humanReadableName} Listings`}>
        <div style={{ display: 'flex' }}>
          <StateSwitcher
            disabled={loading}
            onChange={onStateSwitcherChange}
            sections={TabStates}
            selectedTabId={meta.tab || 'active-all'}
          />
          <Button
            active={meta.tab === 'attention-required'}
            icon={requiresAttention ? IconNames.ERROR : null}
            intent={requiresAttention ? Intent.DANGER : Intent.NONE}
            disabled={loading}
            minimal
            onClick={() => onStateSwitcherChange('attention-required', Meta.FailedStates)}
            style={{ marginLeft: 20 }}
          >
            Attention Required
          </Button>
        </div>
        <Divider style={{ margin: '20px 0 10px' }} />
        <Drawer
          size={Drawer.SIZE_STANDARD}
          isOpen={!!meta.viewListingId}
          enforceFocus
          hasBackdrop
          usePortal
          style={{ overflow: 'scroll' }}
          autoFocus
          canEscapeKeyClose
          canOutsideClickClose
          onClose={closeDrawer}
        >
          <ShowListingComponent
            refreshData={() => {
              // reset the attentionPoller fully because the underlying data (the listings which need attention)
              // could have changed by deleting a listing
              attentionPollerRef?.current?.reset()
              return fetchData(meta)
            }}
            onClose={closeDrawer}
            listingId={meta.viewListingId}
            marketAccount={marketAccount}
            onChange={listingId => openDrawer(listingId)}
          />
        </Drawer>

        <PopIn
          overlayProps={{
            hasBackdrop: false,
            isOpen: selectionState?.selection?.size > 0,
            canEscapeKeyClose: true,
            canOutsideClickClose: false,
            onClose: () => setSelectionState({ selection: new Set(), selectAll: false })
          }}
        >
          <BulkActionBar
            title={`${selectionState?.selection?.size} listing${S(selectionState?.selection?.size)} selected`}
            actions={getAvailableActions(selectionManager)}
          />
        </PopIn>

        <Table
          columns={columns}
          loading={loading}
          meta={meta}
          data={data}
          isSelected={isSelected}
          onMetaChange={onMetaChange}
          toggleAll={onToggleAll}
          toggleSelection={onToggleSelection}
          selectAll={selectionState.selectAll}
          noDataText={meta.tab === 'attention-required' && 'No listings requiring action. Nice work! 🎉'}
          onPageChange={page => onMetaChange('page', nextAllowedPageAfter(page))}
        />
      </Widget>
    </div>
  )
}
